/**
 * Shared utility functions for table row/column freezing.
 *
 * Usage in any table component:
 *   import { isFrozenColumn, getFrozenColumnLeft, ... } from '../../utils/table-freeze.utils';
 *
 * Config keys (JSON):
 *   freezeRow:            boolean            – default true  (header stays sticky on vertical scroll)
 *   freezeColumn:         boolean|(string|number)[] – enable freeze, or directly list columns
 *   freezeColumnCount:    number             – fallback count (default 1)
 *   freezeColumns:        (string|number)[]  – explicit columns by key or index
 *   freezeColumnIndexes:  number[]           – explicit columns by index
 *   freezeColumnWidth: number   – fallback pixel width for index-based tables (default 180)
 *   freezeBodyRows:       (number|string)[]  – explicit sticky body row indexes (0-based),
 *                                              supports -1/"last"/"n-1" for final row
 *   freezeBodyRowIndexes: number[]           – alias for freezeBodyRows
 *   freezeRows:           number[]           – alias for freezeBodyRows
 *   freezeBodyRowCount:   number             – sticky first N body rows
 *   freezeBodyRowHeight:  number             – per-row sticky height (default 28)
 *   freezeHeaderHeight:   number             – sticky header height for body-row top offset (default 28)
 */

// ─── Config Readers ──────────────────────────────────────────────────────────

export function isFreezeColumnEnabled(data: any): boolean {
  return data?.freezeColumn === true || Array.isArray(data?.freezeColumn);
}

export function isFreezeRowDisabled(data: any): boolean {
  return data?.freezeRow === false;
}

export function getFreezeColumnCount(data: any): number {
  const count = Number(data?.freezeColumnCount);
  if (Number.isFinite(count)) {
    return Math.max(0, Math.floor(count));
  }
  return 1;
}

function uniqueSortedNonNegative(values: number[]): number[] {
  return Array.from(new Set(values.filter((v) => Number.isInteger(v) && v >= 0))).sort((a, b) => a - b);
}

function parseNumberArray(value: any): number[] {
  if (!Array.isArray(value)) return [];
  return uniqueSortedNonNegative(
    value
      .map((entry) => Number(entry))
      .filter((entry) => Number.isFinite(entry))
      .map((entry) => Math.floor(entry)),
  );
}

function parseFlexibleRowIndexes(value: any, totalRows: number): number[] {
  if (!Array.isArray(value)) return [];

  const resolved = value
    .map((entry: any) => {
      if (typeof entry === 'number' && Number.isFinite(entry)) {
        const idx = Math.floor(entry);
        return idx < 0 ? totalRows + idx : idx;
      }

      if (typeof entry === 'string') {
        const token = entry.trim().toLowerCase();
        if (token === 'last' || token === 'n-1') {
          return totalRows - 1;
        }
        const asNumber = Number(token);
        if (Number.isFinite(asNumber)) {
          const idx = Math.floor(asNumber);
          return idx < 0 ? totalRows + idx : idx;
        }
      }

      return -1;
    })
    .filter((idx: number) => Number.isInteger(idx) && idx >= 0 && idx < totalRows);

  return Array.from(new Set(resolved)).sort((a, b) => a - b);
}

export function getFrozenColumnIndexes(data: any, displayedColumns: string[] = []): number[] {
  if (!isFreezeColumnEnabled(data)) return [];

  const explicitByKeyOrIndex = Array.isArray(data?.freezeColumns)
    ? data.freezeColumns
    : Array.isArray(data?.freezeColumn)
      ? data.freezeColumn
      : [];
  const explicitByIndex = parseNumberArray(data?.freezeColumnIndexes);

  let indexes: number[] = [];

  if (explicitByKeyOrIndex.length > 0) {
    indexes = uniqueSortedNonNegative(
      explicitByKeyOrIndex
        .map((entry: any) => {
          if (typeof entry === 'number') return Math.floor(entry);
          if (typeof entry === 'string') {
            const asNumber = Number(entry);
            if (Number.isFinite(asNumber) && entry.trim() !== '') {
              return Math.floor(asNumber);
            }
            return displayedColumns.indexOf(entry);
          }
          return -1;
        })
        .filter((idx: number) => idx >= 0),
    );
  } else if (explicitByIndex.length > 0) {
    indexes = explicitByIndex;
  } else {
    const count = getFreezeColumnCount(data);
    for (let i = 0; i < count; i++) {
      indexes.push(i);
    }
  }

  if (displayedColumns.length > 0) {
    return indexes.filter((idx) => idx < displayedColumns.length);
  }
  return indexes;
}

// ─── Column-name based (for mat-table / generic-table / app-table) ──────────

export function isFrozenColumn(
  data: any,
  displayedColumns: string[],
  column: string,
): boolean {
  if (!isFreezeColumnEnabled(data)) return false;
  const idx = displayedColumns.indexOf(column);
  if (idx < 0) return false;
  return getFrozenColumnIndexes(data, displayedColumns).includes(idx);
}

export function isLastFrozenColumn(
  data: any,
  displayedColumns: string[],
  column: string,
): boolean {
  if (!isFreezeColumnEnabled(data)) return false;
  const idx = displayedColumns.indexOf(column);
  if (idx < 0) return false;
  const frozenIndexes = getFrozenColumnIndexes(data, displayedColumns);
  if (!frozenIndexes.includes(idx)) return false;
  return !frozenIndexes.includes(idx + 1);
}

export function getFrozenColumnLeft(
  data: any,
  displayedColumns: string[],
  columnWidths: { [col: string]: number },
  defaultColWidth: number,
  column: string,
): number | null {
  if (!isFreezeColumnEnabled(data)) return null;
  const idx = displayedColumns.indexOf(column);
  const frozenIndexes = getFrozenColumnIndexes(data, displayedColumns);
  if (idx < 0 || !frozenIndexes.includes(idx)) return null;

  let left = 0;
  for (const frozenIdx of frozenIndexes) {
    if (frozenIdx >= idx) break;
    const frozenColumnKey = displayedColumns[frozenIdx];
    left += columnWidths[frozenColumnKey] ?? defaultColWidth;
  }
  return left;
}

// ─── Grouped-header helpers (for generic-table) ─────────────────────────────

export function isFrozenGroup(
  data: any,
  displayedColumns: string[],
  group: { columns?: string[] },
): boolean {
  if (!isFreezeColumnEnabled(data) || !group.columns?.length) return false;
  return group.columns.every((col) => isFrozenColumn(data, displayedColumns, col));
}

export function getFrozenGroupLeft(
  data: any,
  displayedColumns: string[],
  columnWidths: { [col: string]: number },
  defaultColWidth: number,
  group: { columns?: string[] },
): number | null {
  if (!isFreezeColumnEnabled(data) || !group.columns?.length) return null;
  if (!isFrozenGroup(data, displayedColumns, group)) return null;
  return getFrozenColumnLeft(data, displayedColumns, columnWidths, defaultColWidth, group.columns[0]);
}

// ─── Index-based (for plain HTML tables like dynamic-edit-table) ────────────

export function isFrozenColumnIndex(data: any, index: number): boolean {
  if (!isFreezeColumnEnabled(data)) return false;
  return getFrozenColumnIndexes(data).includes(index);
}

export function isLastFrozenColumnIndex(data: any, index: number): boolean {
  if (!isFreezeColumnEnabled(data)) return false;
  const frozenIndexes = getFrozenColumnIndexes(data);
  if (!frozenIndexes.includes(index)) return false;
  return !frozenIndexes.includes(index + 1);
}

export function getFrozenColumnLeftByIndex(
  data: any,
  index: number,
  colWidth?: number,
): number | null {
  if (!isFreezeColumnEnabled(data)) return null;
  const frozenIndexes = getFrozenColumnIndexes(data);
  if (!frozenIndexes.includes(index)) return null;
  const width = colWidth || data?.freezeColumnWidth || 180;
  return frozenIndexes.filter((i) => i < index).length * width;
}

// ─── Sticky Body Rows (optional) ─────────────────────────────────────────────

export function getFrozenBodyRowIndexes(data: any, totalRows: number): number[] {
  const explicitRows = parseFlexibleRowIndexes(
    data?.freezeBodyRows ??
      data?.freezeBodyRowIndexes ??
      data?.freezeRows ??
      (Array.isArray(data?.freezeRow) ? data.freezeRow : undefined),
    totalRows,
  );
  if (explicitRows.length > 0) {
    return explicitRows;
  }

  const freezeCount = Number(data?.freezeBodyRowCount);
  if (Number.isFinite(freezeCount) && freezeCount > 0) {
    const count = Math.min(Math.floor(freezeCount), totalRows);
    return Array.from({ length: count }, (_, i) => i);
  }

  return [];
}

export function isFrozenBodyRow(data: any, rowIndex: number, totalRows: number): boolean {
  return getFrozenBodyRowIndexes(data, totalRows).includes(rowIndex);
}

function getBottomAnchoredFrozenRows(data: any, totalRows: number): number[] {
  const frozenRows = getFrozenBodyRowIndexes(data, totalRows);
  if (frozenRows.length === 0) return [];

  const bottomAnchored: number[] = [];
  let cursor = totalRows - 1;
  while (cursor >= 0 && frozenRows.includes(cursor)) {
    bottomAnchored.push(cursor);
    cursor--;
  }
  return bottomAnchored.sort((a, b) => a - b);
}

export function getFrozenBodyRowTop(data: any, rowIndex: number, totalRows: number): number | null {
  const frozenRows = getFrozenBodyRowIndexes(data, totalRows);
  if (!frozenRows.includes(rowIndex)) return null;

  const bottomAnchored = getBottomAnchoredFrozenRows(data, totalRows);
  if (bottomAnchored.includes(rowIndex)) return null;

  const rowHeight = Number(data?.freezeBodyRowHeight);
  const effectiveRowHeight = Number.isFinite(rowHeight) && rowHeight > 0 ? rowHeight : 28;

  const headerHeight = Number(data?.freezeHeaderHeight);
  const defaultHeaderOffset = Number.isFinite(headerHeight) && headerHeight >= 0 ? headerHeight : 28;
  const headerOffset = isFreezeRowDisabled(data) ? 0 : defaultHeaderOffset;

  const topAnchoredRows = frozenRows.filter((idx) => !bottomAnchored.includes(idx));
  const frozenRowOrder = topAnchoredRows.indexOf(rowIndex);
  return headerOffset + frozenRowOrder * effectiveRowHeight;
}

export function getFrozenBodyRowBottom(data: any, rowIndex: number, totalRows: number): number | null {
  const frozenRows = getFrozenBodyRowIndexes(data, totalRows);
  if (!frozenRows.includes(rowIndex)) return null;

  const bottomAnchored = getBottomAnchoredFrozenRows(data, totalRows);
  if (!bottomAnchored.includes(rowIndex)) return null;

  const rowHeight = Number(data?.freezeBodyRowHeight);
  const effectiveRowHeight = Number.isFinite(rowHeight) && rowHeight > 0 ? rowHeight : 28;

  const orderFromBottom = [...bottomAnchored].reverse().indexOf(rowIndex);
  return orderFromBottom * effectiveRowHeight;
}
